import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { LogoComponent } from '../../components/logo/logo.component';
import { PasswordUpdatedMessageComponent } from '../../components/password-updated-message/password-updated-message.component';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { sanitizeErrorMessage } from '../../shared/utils/error.util';

/**
 * Component that provides a reset-password form allowing a user to set a new password using a token supplied via the route query parameter. It validates password length and matching confirmation, toggles visibility for password fields, calls the AuthService to perform the reset, and displays success or error messages using NotificationService.
 * @component
 * @selector app-reset-password
 * @example
 *   <app-reset-password></app-reset-password>
 * @input {none} none - This component does not accept inputs; it reads the reset token from the URL query parameter 'token'.
 * @output {none} none - This component does not emit outputs; it communicates status via an internal passwordReset flag and NotificationService notifications.
 *
 * Key properties:
 * - passwordReset: boolean - Indicates whether the password has been successfully reset.
 * - resetForm: FormGroup - Reactive form containing 'password' and 'confirmPassword' controls with validation.
 * - token: string | null - Token retrieved from the route query parameters used to authorize the reset.
 * - errorMessage: string | null - Human-readable error message to display when reset fails.
 * - loading: boolean - Indicates an ongoing reset request.
 * - showPassword / showConfirmPassword: boolean - Flags to toggle visibility of the respective input fields.
 *
 * Key methods:
 * - ngOnInit(): void
 *     - Lifecycle hook that retrieves the 'token' from the ActivatedRoute snapshot query parameters.
 * - togglePasswordVisibility(): void
 *     - Toggles the boolean controlling visibility of the password field.
 * - toggleConfirmPasswordVisibility(): void
 *     - Toggles the boolean controlling visibility of the confirm-password field.
 * - passwordMatchValidator(form: FormGroup): void
 *     - Custom form validator that ensures 'password' and 'confirmPassword' values match; sets a 'mismatch' error on the confirmPassword control when they differ.
 * - onSubmit(): void
 *     - Submits the form: if valid and token exists, it calls AuthService.resetPassword(password, token), manages loading state, updates passwordReset on success, and surfaces errors via errorMessage and NotificationService. If token is missing or form invalid, sets an appropriate error and notifies the user.
 * - get password(): AbstractControl | null
 *     - Getter for the password form control.
 * - get confirmPassword(): AbstractControl | null
 *     - Getter for the confirmPassword form control.
 *
 * Important lifecycle hooks:
 * - OnInit: Used to retrieve the reset token from route query parameters before any form submission.
 *
 * Notes:
 * - The component uses ReactiveFormsModule for validation and form handling and relies on AuthService to perform the actual password reset HTTP call.
 * - User-visible notifications are delivered using NotificationService; the component does not emit output events but sets an internal passwordReset flag that can be used in the template to conditionally show success UI.
 */
@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LogoComponent,
    PasswordUpdatedMessageComponent,
    ReactiveFormsModule,
    RouterLink,
  ],
  templateUrl: './reset-password.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent implements OnInit {
  passwordReset = false;
  resetForm: FormGroup;
  token: string | null = null;
  errorMessage: string | null = null;
  loading = false;
  showPassword = false;
  showConfirmPassword = false;

  /**
   * Initializes the reset password component and builds the reactive resetForm FormGroup with password and confirmPassword controls and validation.
   * @example
   * constructor(fb, authService, route, router, messageService)
   * new ResetPasswordComponent(...)
   * @param {{FormBuilder}} {{fb}} - Angular FormBuilder used to create the resetForm FormGroup.
   * @param {{AuthService}} {{authService}} - Service used to perform authentication-related operations.
   * @param {{ActivatedRoute}} {{route}} - ActivatedRoute used to access route parameters.
   * @param {{Router}} {{router}} - Router used for navigation after password reset.
   * @param {{NotificationService}} {{messageService}} - Service used to display user messages and notifications.
   * @returns {{void}} Initializes component state and form controls; does not return a value.
   **/
  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: NotificationService
  ) {
    this.resetForm = this.fb.group(
      {
        password: ['', [Validators.required, Validators.minLength(8)]],
        confirmPassword: ['', [Validators.required]],
      },
      { validator: this.passwordMatchValidator }
    );
  }

  ngOnInit(): void {
    this.token = this.route.snapshot.queryParamMap.get('token');
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password');
    const confirmPassword = form.get('confirmPassword');
    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ mismatch: true });
    } else if (confirmPassword) {
      confirmPassword.setErrors(null);
    }
  }

  /**
   * Submits the password reset request: validates the form, calls authService.resetPassword with the token and new password, manages loading state, and displays success or error messages.
   * @example
   * nSubmit()
   * undefined
   * @param {{void}} none - No parameters.
   * @returns {{void}} No return value.
   **/
  onSubmit() {
    if (this.resetForm.valid && this.token) {
      const { password } = this.resetForm.value;
      this.loading = true;
      this.authService.resetPassword(password, this.token).subscribe({
        next: () => {
          this.passwordReset = true;
          this.messageService.add({
            severity: 'success',
            summary: 'Password reset successful',
            detail: 'Your password has been updated successfully.',
            life: 3000,
          });
          this.loading = false;
        },
        error: err => {
          this.errorMessage = sanitizeErrorMessage(
            err.error?.message || 'An error occurred while resetting your password.'
          );
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: this.errorMessage,
            life: 3000,
          });
        },
        complete: () => {
          this.loading = false;
        },
      });
    } else {
      this.errorMessage = sanitizeErrorMessage('Invalid url or expired');
      this.loading = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: this.errorMessage,
        life: 3000,
      });
    }
  }

  get password() {
    return this.resetForm.get('password');
  }

  get confirmPassword() {
    return this.resetForm.get('confirmPassword');
  }
}
